import type { FunctionComponent } from 'preact'
import { useState } from 'preact/hooks'
import { ChevronDown, Wrench } from 'lucide-preact'
import type { ActivityStep } from '@/types/aiChat'

/**
 * The tool calls behind one answer, one row per call.
 *
 * Every value shown here — the tool name, its arguments, its result, its tokens, its model and
 * its cost — already arrived on the response and used to be discarded. Nothing on this surface
 * is inferred or invented: a row exists because a call was made, and the row says only what that
 * call actually did. That is the whole point of replacing the phrase rotator.
 *
 * Rows expand in place rather than into a modal, matching DeliveryRow — PRODUCT.md treats a modal
 * as a confession that the surface could not handle the job.
 */

const tokenFormatter = new Intl.NumberFormat('en-US')

/** Costs here are fractions of a cent, so the usual 2-decimal money format rounds every one of
 *  them to $0.00. Four decimals keeps a single tool call legible without pretending to more
 *  precision than the price book has. */
const costFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
})

/** Sub-second reads better as milliseconds; past that, one decimal of seconds is enough to
 *  compare two steps without implying stopwatch precision. */
function formatDuration(milliseconds: number): string {
  return milliseconds < 1000 ? `${milliseconds}ms` : `${(milliseconds / 1000).toFixed(1)}s`
}

/** Pretty-print a JSON payload for the expanded detail, falling back to the raw string when it
 *  does not parse. A tool result is whatever the executor returned; it is not guaranteed JSON. */
function formatPayload(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    return raw
  }
}

/** Results can be lead lists. Cap what is rendered so one row cannot blow out the scroll
 *  container, and say plainly that it was cut rather than trailing off. */
const MAX_PAYLOAD_CHARS = 4000

function truncate(value: string): { text: string; truncated: boolean } {
  return value.length > MAX_PAYLOAD_CHARS
    ? { text: value.slice(0, MAX_PAYLOAD_CHARS), truncated: true }
    : { text: value, truncated: false }
}

interface ActivityStreamProps {
  steps: ActivityStep[]
}

export const ActivityStream: FunctionComponent<ActivityStreamProps> = ({ steps }) => {
  if (steps.length === 0) return null

  return (
    <div className="mb-2 flex flex-col gap-0.5" data-testid="activity-stream">
      <div className="text-[10.5px] font-semibold tracking-wider uppercase text-slate-400 dark:text-slate-500 px-1 pb-0.5">
        {steps.length} {steps.length === 1 ? 'step' : 'steps'}
      </div>
      {steps.map(step => (
        <ActivityRow key={step.id} step={step} />
      ))}
    </div>
  )
}

const ActivityRow: FunctionComponent<{ step: ActivityStep }> = ({ step }) => {
  const [expanded, setExpanded] = useState(false)

  const toggle = () => setExpanded(v => !v)
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      toggle()
    }
  }

  const args = truncate(formatPayload(step.arguments))
  const result = step.result === null ? null : truncate(formatPayload(step.result))

  // A step the API recorded no figures for gets no footer at all — an empty rule under the
  // payload reads as a rendering fault rather than as "nothing was recorded".
  const hasTelemetry =
    step.modelId != null ||
    step.durationMs != null ||
    step.inputTokens != null ||
    step.outputTokens != null ||
    step.costUsd != null

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={toggle}
        onKeyDown={onKeyDown}
        className="grid grid-cols-[16px_1fr_auto_16px] items-center gap-2 px-2 py-1.5 rounded-lg text-xs cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors duration-150"
      >
        <Wrench className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500" aria-hidden="true" />
        <span className="font-mono text-slate-700 dark:text-slate-300 truncate">{step.name}</span>
        <span className="text-[11px] text-slate-400 dark:text-slate-500 tabular-nums text-right whitespace-nowrap">
          {step.result === null && (
            <span className="text-amber-600 dark:text-amber-500">did not run</span>
          )}
          {/* A failed tool looks identical to one that returned an empty list unless it says so. */}
          {step.result !== null && step.outcome === 'Failed' && (
            <span className="text-red-600 dark:text-red-400">failed</span>
          )}
          {step.result !== null && step.outcome !== 'Failed' && step.durationMs != null && (
            <>{formatDuration(step.durationMs)}</>
          )}
        </span>
        <ChevronDown
          className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-150 ${expanded ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </div>

      {expanded && (
        <div className="mx-2 mb-2 px-3 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex flex-col gap-2">
          <DetailBlock label="Arguments" payload={args} />
          {result === null ? (
            <DetailLine
              label="Result"
              value="The turn ended before this tool returned."
            />
          ) : (
            <DetailBlock label="Result" payload={result} />
          )}
          {hasTelemetry && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1 border-t border-slate-200 dark:border-slate-700">
              {step.modelId && <DetailLine label="Model" value={step.modelId} />}
              {step.durationMs != null && (
                <DetailLine label="Took" value={formatDuration(step.durationMs)} />
              )}
              {step.inputTokens != null && (
                <DetailLine label="In" value={`${tokenFormatter.format(step.inputTokens)} tok`} />
              )}
              {step.outputTokens != null && (
                <DetailLine label="Out" value={`${tokenFormatter.format(step.outputTokens)} tok`} />
              )}
              {step.costUsd != null && (
                <DetailLine
                  label="Cost"
                  value={
                    step.batchSize > 1
                      ? `${costFormatter.format(step.costUsd)} for all ${step.batchSize} calls`
                      : costFormatter.format(step.costUsd)
                  }
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const DetailBlock: FunctionComponent<{
  label: string
  payload: { text: string; truncated: boolean }
}> = ({ label, payload }) => (
  <div className="flex flex-col gap-1">
    <span className="text-[10.5px] font-semibold tracking-wider uppercase text-slate-400 dark:text-slate-500">
      {label}
    </span>
    <pre className="font-mono text-[11px] leading-relaxed text-slate-700 dark:text-slate-300 whitespace-pre-wrap break-words max-h-56 overflow-y-auto m-0">
      {payload.text}
    </pre>
    {payload.truncated && (
      <span className="text-[10.5px] italic text-slate-400 dark:text-slate-500">
        Truncated at {tokenFormatter.format(MAX_PAYLOAD_CHARS)} characters.
      </span>
    )}
  </div>
)

const DetailLine: FunctionComponent<{ label: string; value: string }> = ({ label, value }) => (
  <span className="flex items-baseline gap-1.5">
    <span className="text-[10.5px] font-semibold tracking-wider uppercase text-slate-400 dark:text-slate-500">
      {label}
    </span>
    <span className="font-mono text-[11px] text-slate-700 dark:text-slate-300 break-words">
      {value}
    </span>
  </span>
)
